import React, { useEffect, useState } from 'react';
import DateSlots from './DateSlots';
import TimeSlots from './TimeSlots';
import InterviewForm from './InterviewForm';
import { generateDateTimeSlots } from 'src/utils/recruiter.util';
import { ScheduleInterviewRoundInput, UIApplication, UIDateSlot, UserRole } from 'types';
import { useMutation, useQueryClient } from 'react-query';
import {
	generateMeetingLink,
	rescheduleInterviewRoundForApplication,
	scheduleInterviewRoundForApplication,
} from 'src/apis/application';
import { useToast } from 'src/context/ToastProvider';
import { get } from 'lodash';
import { LocalizationProvider, MobileDateTimePicker } from '@mui/x-date-pickers';
import { Button } from 'flowbite-react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { isUserAccountManager } from 'shared-utils';
import { useUserContext } from 'src/context/UserProvider';
interface Props {
	userName: string;
	userId?: string;
	positionId?: string;
	onFinish?: () => void;
	isReschedule?: boolean;
	application?: UIApplication;
	isLoading?: boolean;
	onSubmit?: (data: ScheduleInterviewRoundInput) => void;
}
const ScheduleInterview = ({
	userName,
	userId,
	positionId,
	onFinish,
	isReschedule,
	application,
	isLoading,
	onSubmit,
}: Props) => {
	const queryClient = useQueryClient();
	const { user } = useUserContext();
	const { dates, times, todaysTimes } = generateDateTimeSlots();
	const [showCustomDate, setShowCustomDate] = useState<boolean>(false);
	const [customDate, setCustomDate] = useState<Date>();

	const [scheduleInterViewRoundInput, setScheduleInterViewRoundInput] = useState<ScheduleInterviewRoundInput>({
		positionId: positionId || '',
		userId: userId || '',
		startTime: new Date(),
		duration: application?.ongoingRound?.duration || 0,
		message: application?.ongoingRound?.description || '',
		interviewLink: application?.ongoingRound?.interviewOrAssignmentLink || '',
		interviewers: application?.ongoingRound?.interviewers || [],
	});

	const [selectedDateSlot, setSelectedDateSlot] = useState<UIDateSlot>();
	const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('');

	useEffect(() => {
		setCustomDate(undefined);
		if (!selectedDateSlot || !selectedTimeSlot) {
			return;
		}
		let startDateAndTime = new Date(selectedDateSlot?.fullDate);
		// Extract the hour and minute from the time string
		let [timePart, modifier] = selectedTimeSlot.split(' ');
		let [hours, minutes] = timePart.split(':').map(str => parseInt(str, 10));
		// Convert 12-hour time to 24-hour format
		if (modifier === 'PM' && hours < 12) {
			hours += 12;
		} else if (modifier === 'AM' && hours === 12) {
			hours = 0;
		}
		// Set the hours and minutes to the date object
		startDateAndTime.setHours(hours, minutes, 0, 0); // Resetting seconds and milliseconds
		setScheduleInterViewRoundInput(prev => ({ ...prev, startTime: startDateAndTime }));
	}, [selectedDateSlot, selectedTimeSlot]);

	useEffect(() => {
		if (customDate) {
			setScheduleInterViewRoundInput(prev => ({ ...prev, startTime: customDate }));
		}
	}, [customDate]);

	const toast = useToast();

	const scheduleInterviewFn = useMutation(
		['scheduleInterviewRoundForApplication'],
		scheduleInterviewRoundForApplication,
		{
			onSuccess() {
				queryClient.refetchQueries({ queryKey: ['getRecommendationsForPosition'], exact: false });
				toast({
					title: 'Interview scheduled successfully',
					type: 'success',
				});
				onFinish && onFinish();
			},
			onError(error: Error) {
				const errorMessage = get(error, ['response', 'data', 'message'], error.message);
				toast({
					title: `Error occured while scheduling interview ${errorMessage}`,
					type: 'error',
				});
			},
		},
	);
	const rescheduleInterviewFn = useMutation(
		['rescheduleInterviewRoundForApplication'],
		rescheduleInterviewRoundForApplication,
		{
			onSuccess() {
				queryClient.refetchQueries({ queryKey: ['getRecommendationsForPosition'], exact: false });
				toast({
					title: 'Interview rescheduled successfully',
					type: 'success',
				});
				onFinish && onFinish();
			},
			onError(error: Error) {
				const errorMessage = get(error, ['response', 'data', 'message'], error.message);
				toast({
					title: `Error occured while rescheduling interview ${errorMessage}`,
					type: 'error',
				});
			},
		},
	);

	useEffect(() => {
		setSelectedTimeSlot('');
		setCustomDate(undefined);
	}, [selectedDateSlot]);

	return (
		<div className="flex flex-col gap-[20px]">
			<p className="font-inter text-[18px] font-[500] leading-[28px]">
				{isReschedule ? 'Reschedule Interview for:' : 'Interviewing:'} <span className="font-[700]">{userName}</span>
			</p>

			<div className="relative">
				{isUserAccountManager(user?.role as UserRole) && (
					<>
						<Button
							color="light"
							className="absolute right-0 top-[-4px]"
							size="xs"
							onClick={() => setShowCustomDate(true)}
						>
							Custom Date
						</Button>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<MobileDateTimePicker
								open={showCustomDate}
								onClose={() => setShowCustomDate(false)}
								onChange={(value: any, context: any) => {
									setSelectedTimeSlot('');
									setSelectedDateSlot(undefined);
									setCustomDate(value['$d']);
								}}
								slots={{ textField: () => null }}
								slotProps={{
									dialog: {
										sx: {
											'& .MuiDialog-paper': {
												borderRadius: '16px', // You can adjust this value
											},
										},
									},
								}}
							/>
						</LocalizationProvider>
					</>
				)}

				<DateSlots
					dates={dates}
					times={times}
					selectedDateSlot={selectedDateSlot}
					setSelectedDateSlot={setSelectedDateSlot}
					setSelectedTimeSlot={setSelectedTimeSlot}
				/>
				<TimeSlots
					dates={dates}
					times={times}
					todaysTimes={todaysTimes}
					selectedDateSlot={selectedDateSlot}
					selectedTimeSlot={selectedTimeSlot}
					setSelectedTimeSlot={setSelectedTimeSlot}
				/>
			</div>

			<InterviewForm
				isReschedule={isReschedule}
				isLoading={scheduleInterviewFn.isLoading || rescheduleInterviewFn.isLoading || isLoading}
				scheduleInterViewRoundInput={scheduleInterViewRoundInput}
				setScheduleInterViewRoundInput={setScheduleInterViewRoundInput}
				scheduleInterviewFn={scheduleInterviewFn}
				disable={!customDate && (!selectedDateSlot || !selectedTimeSlot)}
				rescheduleInterviewFn={rescheduleInterviewFn}
				onSubmit={onSubmit}
			/>
		</div>
	);
};

export default ScheduleInterview;
